import { fabric } from 'fabric'
import type { FrameSize } from '../frame'
import WbFrame, { frameSizes } from '../frame'
import WbArticleImage from '../articleImage'
import WbImage from '../image'
import WbModelDetails from '../modelDetails'
import WbTextBox from '../textBox'
import type { IWhiteboardTemplate, IWhiteboardTemplateFileMapping, IWhiteboardTemplateOption } from './IWhiteboardTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, whiteboardConstants } from '@/models/constants'
import type MyArticle from '@/models/myArticle'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import utils from '@/services/utils'
import apparelTechSheetImage from '@/assets/templateLayoutImages/apparelTechSheet.png'
import type LinkedCustomer from '@/models/linkedCustomer'

class ApparelTechSheet implements IWhiteboardTemplate {
  id = whiteboardConstants.frameTemplatesId.apparelTechSheet
  name = 'Apparel Tech Sheet'
  layoutImage = apparelTechSheetImage
  settings = {
    articleImage: { width: 500, height: 500, scale: 90 },
    articleDetails: { fontFamily: 'Neue Plak', fontSize: 14, fontWeight: 'normal', fill: '#ffffff', textAlign: 'left', showLabels: false },
    modelDetails: { width: 325, fontFamily: 'Neue Plak', fontSize: 14, fontWeight: 'normal', fill: '#ffffff', textAlign: 'left', attributes: [], attributePlaceHolder: '[Blank]', showLabels: false },
    textBox: { fontFamily: 'Neue Plak', fontSize: 14, fontWeight: 'normal', fontStyle: 'normal', fill: '#ffffff', textAlign: 'left' },
  }

  imgSize: number = 500
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  frameWidth: number = frameSizes.wide.width
  frameHeight: number = frameSizes.wide.height
  logoWidth = 155
  logoHeight = 87
  logoScaling = 20
  uaNameLogoWidth = 400
  uaNameLogoHeight = 38
  uaNameLogoScaling = 40
  leftMarginForModel = 30
  startPositionX = 0
  startPositionY = 0
  rightMargin = 20
  frameSpacing = 50
  leftPadding: number = 50
  rightPadding: number = 30
  articleImageProps = {
    bottomPadding: 50,
  }

  modelNameProps = {
    fontSize: 54,
    fontWeight: 'bold',
    topPadding: 120,
    leftPadding: 120,
    bottomPadding: 30,
    rightPadding: 30,
  }

  modelDetailsProps = {
    padding: 5,
  }

  headerImageNameLogoProps = {
    bottomPadding: 180,
  }

  textBoxProps = {
    width: 325,
    topPadding: 20,
    bottomPadding: 10,
    leftPadding: 120,
  }

  needToKnowstextBoxProps = {
    width: 310,
    topPadding: 10,
    bottomPadding: 130,
    leftPadding: 120,
    fontSize: 34,
    fontWeight: 'bold',
    textAlign: 'left',
    fill: '#ffffff',
    fontStyle: 'normal',
  }

  ponitsTextBoxProps = {
    topPadding: 40,
    leftPadding: 350,
  }

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, defaultImageTypeForTemplates: string): IWhiteboardTemplateOption[] {
    const modelAttributesWithSKU: IMyAttribute[] = []
    const options: IWhiteboardTemplateOption[] = []
    const defaultDisplayAttributesList = ['Department', 'Class', 'ModelLifecycle']
    const defaultAllowedDisplayAttributesList = defaultDisplayAttributesList.filter(attribute => myAttributes[attribute])
    defaultAllowedDisplayAttributesList.push(catalog.CatalogCode.toString())
    // model attributes
    const modellevelAttributes = Object.values(myAttributes).filter(attr => attr.IsModelLevel)
    for (const attr of modellevelAttributes) {
      modelAttributesWithSKU.push(attr)
    }

    // add current season SKU count
    const SKUField = { ...appConstants.staticFieldTemplate }
    SKUField.DisplayName = `${catalog.Season} SKU`
    SKUField.SystemName = catalog.CatalogCode.toString()
    SKUField.IsModelLevel = true
    SKUField.IsSKU = true
    modelAttributesWithSKU.push(SKUField)

    options.push({
      name: 'displayAttributes',
      label: 'generateFrameDialog.steps.options.modelAttributesToDisplay',
      type: 'attributeChooser',
      default: defaultAllowedDisplayAttributesList,
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 5,
      data: modelAttributesWithSKU,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: defaultImageTypeForTemplates,
      data: whiteboardConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })

    // const availableFrameSizes: { key: FrameSize, label: string }[] = [
    //   { key: 'wide', label: 'Wide' },
    //   { key: 'a4', label: 'A4' },
    //   { key: 'letter', label: 'Letter' },
    // ]

    // options.push({
    //   name: 'frameSize',
    //   label: 'generateFrameDialog.steps.options.frameSize',
    //   type: 'list',
    //   default: 'wide',
    //   required: true,
    //   multiple: false,
    //   data: availableFrameSizes,
    //   valueProp: 'key',
    //   displayProp: 'label',
    // })

    return options
  }

  getFileMapping(): IWhiteboardTemplateFileMapping[] {
    return [{
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    }]
  }

  async generate(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articlesData: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, currentCustomer: LinkedCustomer | undefined, startPosition: IPoint, direction: 'horizontal' | 'vertical', excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    this.frameSpacing = (direction === 'vertical') ? 100 : 50
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, startPosition, direction, retailPg, wholesalePg, outletPg, isExporting)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, startPosition: IPoint, direction: 'horizontal' | 'vertical', retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    const objects: IWbObject[] = []
    this.imgSize = (this.settings.articleImage.width * this.settings.articleImage.scale) / 100
    this.startPositionX = isExporting ? 0 : startPosition.x ? startPosition.x : 0
    this.startPositionY = isExporting ? 0 : startPosition.y ? startPosition.y : 0
    const frameSize = 'wide' // options.frameSize
    this.settings.modelDetails.attributes = options.displayAttributes

    const modelsMap = new Set<string>()
    const uniqueModelArticles: MyArticle[] = []
    for (const article of articles) {
      if (!modelsMap.has(article.ModelNumber)) {
        modelsMap.add(article.ModelNumber)
        uniqueModelArticles.push(article)
      }
    }

    let frameLeft = this.startPositionX
    let frameTop = this.startPositionY
    let frameCount = 0
    this.changePropsAsPerFrameSize(frameSize)

    const verticalSectionPosition = this.frameWidth / 2
    const horizontalSectionByHeight = this.frameHeight / 2

    for (const firstArticle of uniqueModelArticles) {
      const frameObjects: IWbObject[] = []

      frameObjects.push(await this.createHeaderLogo(
        frameTop + this.leftPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      frameObjects.push(await this.createHeaderNameLogo(
        frameTop + this.frameHeight - this.headerImageNameLogoProps.bottomPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      const modelNameLeft = frameLeft + this.modelNameProps.leftPadding
      const modelNameTop = frameTop + this.modelNameProps.topPadding
      const modelNameOpt = {
        left: modelNameLeft,
        top: modelNameTop,
        showLabels: this.settings.modelDetails.showLabels,
        width: verticalSectionPosition - this.modelNameProps.rightPadding,
        attributes: ['ModelName'],
        attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
        fontFamily: this.settings.modelDetails.fontFamily,
        fontSize: this.modelNameProps.fontSize,
        fontWeight: this.modelNameProps.fontWeight,
        textAlign: this.settings.modelDetails.textAlign,
        fill: this.settings.modelDetails.fill,
      }
      const modelNameObject = await WbModelDetails.loadModelDetails(firstArticle, modelNameOpt)
      frameObjects.push(isExporting ? modelNameObject.toObject() as WbModelDetails : modelNameObject)

      const modelDetailsTop = (modelNameObject && modelNameObject.top && modelNameObject.height ? modelNameObject.top + modelNameObject.height : frameTop + this.frameHeight / 2) + this.modelDetailsProps.padding
      const modelDetailsOpt = {
        left: modelNameLeft,
        top: modelDetailsTop,
        showLabels: this.settings.modelDetails.showLabels,
        width: this.settings.modelDetails.width,
        attributes: ['ModelNumber', '_RetailPrice'],
        attributesSeparator: ' // ',
        attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
        fontFamily: this.settings.modelDetails.fontFamily,
        fontSize: this.settings.modelDetails.fontSize,
        fontWeight: this.settings.modelDetails.fontWeight,
        textAlign: this.settings.modelDetails.textAlign,
        fill: this.settings.modelDetails.fill,
      }
      const modelDetailsObject = await WbModelDetails.loadModelDetails(firstArticle, modelDetailsOpt)
      frameObjects.push(isExporting ? modelDetailsObject.toObject() as WbModelDetails : modelDetailsObject)

      const attributeObjectTop = (modelDetailsObject && modelDetailsObject.top && modelDetailsObject.height ? modelDetailsObject.top + modelDetailsObject.height : frameTop + this.frameHeight / 2) + this.modelDetailsProps.padding
      if (this.settings.modelDetails.attributes.length) {
        const attributesTop = attributeObjectTop
        frameObjects.push(
          ...await this.createArticleAttributes(
            firstArticle,
            this.settings.modelDetails.attributes,
            attributesTop,
            modelNameLeft,
            isExporting,
            myAttributes,
            catalog,
          ),
        )
      }

      const needToKnowsTextBoxTop = frameTop + horizontalSectionByHeight + 20
      const needToKnowsTextBox: any = this.createTextObject(
        'NEED TO KNOWS',
        needToKnowsTextBoxTop,
        modelNameLeft,
        verticalSectionPosition - this.needToKnowstextBoxProps.leftPadding,
        0,
        this.needToKnowstextBoxProps.fontSize,
        this.needToKnowstextBoxProps.fontStyle,
        this.needToKnowstextBoxProps.fontWeight,
        'left',
        this.needToKnowstextBoxProps.fill,
      )
      frameObjects.push(needToKnowsTextBox)

      const pointsTextBoxTop = needToKnowsTextBoxTop + this.ponitsTextBoxProps.topPadding
      const pointsTextBox: any = this.createTextObject(
        'Enter text here',
        pointsTextBoxTop,
        modelNameLeft,
        verticalSectionPosition - this.textBoxProps.leftPadding,
        0,
        this.settings.textBox.fontSize,
        this.settings.textBox.fontStyle,
        this.settings.textBox.fontWeight,
        this.settings.textBox.textAlign,
        this.settings.textBox.fill,
      )
      frameObjects.push(pointsTextBox)

      const imgLeft = frameLeft + verticalSectionPosition + ((this.frameWidth - verticalSectionPosition) / 2) - (this.imgSize / 2)
      const imgTop = frameTop + (this.frameHeight / 2) - (this.imgSize / 2)
      frameObjects.push(await this.createImage(
        imgTop,
        imgLeft,
        firstArticle,
        this.settings.articleImage.scale,
        options.imageType,
        isExporting,
      ))

      objects.push(...frameObjects)
      objects.push(new WbFrame(
        frameSize,
        {
          children: frameObjects.map(obj => obj.id || ''),
          name: firstArticle.ModelName,
          left: frameLeft,
          top: frameTop,
          sortOrder: frameCount,
          lock: false,
          fill: '#000000',
        },
      ))

      if (direction === 'vertical') {
        frameTop += isExporting ? 0 : this.frameHeight + this.frameSpacing
      }
      else {
        frameLeft += isExporting ? 0 : this.frameWidth + this.frameSpacing
      }
      frameCount++
    }

    return objects
  }

  changePropsAsPerFrameSize(frameSize: FrameSize) {
    this.frameWidth = frameSizes[frameSize].width
    this.frameHeight = frameSizes[frameSize].height
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const minNumberOfLines = Math.ceil(utils.getTextWidth(text, `${fontWeight} ${fontSize}px ${font}`) / availableWidth)
    let multiLineSampleText = ''
    for (let index = 0; index < minNumberOfLines; index++) {
      multiLineSampleText += text
      if (index < minNumberOfLines - 1) {
        multiLineSampleText += '\n'
      }
    }
    return utils.getTextHeight(multiLineSampleText, fontSize, font, fabric)
  }

  async createHeaderNameLogo(top: number, left: number, isExporting: boolean = false) {
    const options = {
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
      scaleX: this.uaNameLogoScaling / 100,
      scaleY: this.uaNameLogoScaling / 100,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaNameLogoWhite, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaNameLogoWhite, options)
    }
  }

  async createHeaderLogo(top: number, left: number, isExporting: boolean = false) {
    const options = {
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
      scaleX: this.logoScaling / 100,
      scaleY: this.logoScaling / 100,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaIconWhite, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaIconWhite, options)
    }
  }

  async createImage(top: number, left: number, article: MyArticle, imageScaleFactor: number, imageType: string, isExporting: boolean) {
    const opt = {
      top,
      left,
      catalogCode: article.CatalogCode,
      articleId: article.Id,
      objectId: article.CatalogArticleId,
      isRequest: article._IsRequestArticle,
      assetKey: imageType,
      scaleX: imageScaleFactor / 100,
      scaleY: imageScaleFactor / 100,
    }
    if (isExporting) {
      return await WbArticleImage.getJsonObject(article, this.settings.articleImage.width, this.settings.articleImage.height, opt)
    }
    else {
      return await WbArticleImage.loadArticleImage(article, this.settings.articleImage.width, this.settings.articleImage.height, opt)
    }
  }

  createTextObject(text: string, top: number, left: number, width: number, height: number, fontSize: number, fontStyle, fontWeight: string, textAlign: string, fill: string) {
    text = text != null ? text.toString() : ''
    return new WbTextBox(text, {
      top,
      left,
      width,
      height,
      fill,
      fontFamily: this.settings.textBox.fontFamily,
      fontSize,
      fontStyle,
      fontWeight,
      textAlign,
      lock: false,
    })
  }

  async createArticleAttributes(article: MyArticle, articleAttributes: string[], top: number, left: number, isExporting: boolean = false, myAttributes: Record<string, IMyAttribute>, catalog: CatalogDetails) {
    const attributeObjects: IWbObject[] = []
    const modelAttributesWithSKU: Record<string, IMyAttribute> = {}
    const modellevelAttributes = Object.values(myAttributes).filter(attr => attr.IsModelLevel)
    for (const key in modellevelAttributes) {
      modelAttributesWithSKU[key] = myAttributes[key]
    }
    // add current season SKU count
    const SKUField = { ...appConstants.staticFieldTemplate }
    SKUField.DisplayName = `${catalog.Season} SKU`
    SKUField.SystemName = catalog.CatalogCode.toString()
    SKUField.IsModelLevel = true
    SKUField.IsSKU = true
    modelAttributesWithSKU[catalog.CatalogCode.toString()] = SKUField

    const headerOpt = {
      left,
      top,
      showLabels: this.settings.modelDetails.showLabels,
      width: this.settings.modelDetails.width,
      attributes: articleAttributes,
      attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
      fontFamily: this.settings.modelDetails.fontFamily,
      fontSize: this.settings.modelDetails.fontSize,
      fontWeight: this.settings.modelDetails.fontWeight,
      textAlign: this.settings.modelDetails.textAlign,
      fill: this.settings.modelDetails.fill,
    }
    const modelObject = await WbModelDetails.loadModelDetails(article, headerOpt)
    attributeObjects.push(isExporting ? modelObject.toObject() as WbModelDetails : modelObject)

    return attributeObjects
  }
}

export default new ApparelTechSheet()
